import {
  CAMERA_PRESENTER,
  DocSourceTypes,
  getSourceValue,
  UPLOADED,
  UPLOADING,
  UPLOAD_FAILED,
  isVideoSource,
  SOURCE_MAX_FILE_SIZE_MULTIPART
} from '@shoflo/polymer';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { readFileAsBinary } from '../../utils/actionUtils';
import {
  showNotification,
  showUploadStatus,
  setSourceUploadStatus,
  setDesiredAttendeeMuteStatus
} from '@shoflo/studio-redux/actions';
import { BROADCAST_CREATE_SOURCE } from '@shoflo/studio-redux/actions/types';
import {
  withCurrentParams,
  uploadToPreSignedURL,
  handleMultipartUpload
} from '@shoflo/studio-redux/utils';
import { selectLastPresenterSource } from '@shoflo/studio-redux/selectors';
import { DEFAULT_ORDER_NUMBER } from 'utils/source';
import fileTypes from '@shoflo/studio-redux/constants/FileTypes';
import { SOURCE_CREATE_ERROR } from '@studio/constants/NotificationErrors';

const logger = new StudioLogger('Source');

/**
 *
 * @param {*} source
 * @param isInitializing
 * @param contentUploadedTrackerCallback
 * @param startFileUploadActionCallback
 */
function createSource(
  source,
  isInitializing = false,
  contentUploadedTrackerCallback,
  startFileUploadActionCallback
) {
  return withCurrentParams(
    (dispatch, { teamId, eventId, broadcastId }, getState) => {
      // Do not attempt to create the action when source type is undefined
      if (!source.type) {
        logger.error('Source type is undefined', source);
        console.error('Unable to create source, source type is undefined');
        return dispatch(
          showNotification(SOURCE_CREATE_ERROR, {
            type: 'warning'
          })
        );
      }

      const action = {
        type: BROADCAST_CREATE_SOURCE,
        request: async apiClient => {
          const { name: fileName = '' } = source.value || {};

          const order = getNewSourceOrder(getState);

          // Get the data needed for the payload from the source
          const {
            value,
            description = '',
            type,
            ext,
            title,
            photo,
            userId,
            role,
            presenterSessionId,
            cameraUnavailable,
            fileSize
          } = source;
          const payload = {
            order,
            value,
            description,
            type,
            ext,
            title,
            photo,
            userId,
            role,
            cameraUnavailable
          };
          let externalVideoId = null;
          try {
            //If it's a doc type, we need to create a new event doc so we can assign it as the value
            if (DocSourceTypes.includes(source.type)) {
              dispatch(setSourceUploadStatus(fileName, UPLOADING));
              dispatch(showUploadStatus());
              let eventDocumentId;
              // Use multi-part upload if the file is a video and its size is greater than 40 MB
              const isMultiPartUpload =
                isVideoSource(source) &&
                fileSize > SOURCE_MAX_FILE_SIZE_MULTIPART;
              const fileUploadActionPayload = {
                title: source.title,
                fileType: source.type,
                extension: source.ext,
                isDragAndDrop: source.isDragAndDrop ?? false,
                isMultiFileUpload: source.isMultiFileUpload ?? false,
                fileSizeInBytes: fileSize
              };
              const startFileUploadCallback = data => {
                startFileUploadActionCallback({
                  ...data,
                  ...fileUploadActionPayload
                });
              };
              if (isMultiPartUpload) {
                const uploadResponse = await handleMultipartUpload(
                  apiClient,
                  teamId,
                  eventId,
                  {
                    name: fileName,
                    description: 'Media Content',
                    isStudioMedia: true,
                    isMultipartUpload: true
                  },
                  source.value,
                  startFileUploadCallback
                );
                externalVideoId = uploadResponse.externalVideoId;
                eventDocumentId = uploadResponse.eventDocId;
              } else {
                const createDocumentResponse =
                  await apiClient.eventDoc.createDocument(teamId, eventId, {
                    name: fileName,
                    description: 'Media Content',
                    isStudioMedia: true
                  });
                const {
                  _id: eventDocId,
                  preSignedUploadUrl,
                  preSignedUploadHeaders
                } = createDocumentResponse;

                externalVideoId = createDocumentResponse.externalVideoId;
                // start file upload sli
                startFileUploadCallback({
                  externalVideoId: externalVideoId
                });
                const binaryData = await readFileAsBinary(source.value);
                const fileExtension = fileName.split('.').pop().toLowerCase();
                const contentType = fileTypes[fileExtension][0];
                const uploadHeaders =
                  preSignedUploadHeaders &&
                  preSignedUploadHeaders['x-amz-tagging']?.join(',');
                await uploadToPreSignedURL(
                  preSignedUploadUrl,
                  binaryData,
                  contentType,
                  uploadHeaders
                );
                eventDocumentId = eventDocId;
              }

              payload.value = {
                eventDocId: eventDocumentId,
                ...(isVideoSource(source) && {
                  transcodingStatus: 'processing'
                })
              };
            }

            let result = await apiClient.broadcast.createSource(
              teamId,
              eventId,
              broadcastId,
              payload
            );

            if (DocSourceTypes.includes(source.type)) {
              dispatch(setSourceUploadStatus(fileName, UPLOADED));
              contentUploadedTrackerCallback?.({
                title: source.title,
                fileType: source.type,
                extension: source.ext,
                isDragAndDrop: source.isDragAndDrop ?? false,
                isMultiFileUpload: source.isMultiFileUpload ?? false,
                externalVideoId: externalVideoId,
                fileSizeInBytes: fileSize
              });
            }

            if (result.type === CAMERA_PRESENTER) {
              // New camera sources are muted by default
              const sourceValue = getSourceValue(result);
              dispatch(setDesiredAttendeeMuteStatus(sourceValue, true));
            }

            return {
              ...result,
              isInitializing,
              presenterSessionId
            };
          } catch (error) {
            dispatch(setSourceUploadStatus(fileName, UPLOAD_FAILED));
            if (DocSourceTypes.includes(source.type)) {
              contentUploadedTrackerCallback?.({
                title: source.title,
                fileType: source.type,
                extension: source.ext,
                isDragAndDrop: source.isDragAndDrop ?? false,
                isMultiFileUpload: source.isMultiFileUpload ?? false,
                externalVideoId: externalVideoId,
                fileSizeInBytes: fileSize,
                error: error
              });
            }
            dispatch(
              showNotification(SOURCE_CREATE_ERROR, {
                type: 'warning'
              })
            );
            throw error;
          }
        }
      };

      dispatch(action);
    }
  );
}

export default createSource;

function getNewSourceOrder(getState) {
  let { order: lastPresenterOrder = DEFAULT_ORDER_NUMBER } =
    selectLastPresenterSource(getState());

  return lastPresenterOrder + 1;
}
